import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import api, { getAdminPin, setAdminPin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LockKey, ShieldCheck } from "@phosphor-icons/react";

export function useAdminUnlocked() {
  const { user } = useAuth();
  const noPin = user && typeof user === "object" && user.org && user.org.admin_pin_required === false;
  const [unlocked, setUnlocked] = useState(!!getAdminPin());
  useEffect(() => {
    const on = () => setUnlocked(!!getAdminPin());
    window.addEventListener("admin-locked", on);
    window.addEventListener("admin-unlocked", on);
    return () => { window.removeEventListener("admin-locked", on); window.removeEventListener("admin-unlocked", on); };
  }, []);
  return unlocked || !!noPin;
}

export default function AdminGate() {
  const unlocked = useAdminUnlocked();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pin.trim()) return;
    setBusy(true); setError("");
    try {
      await api.post("/admin/unlock", { pin: pin.trim() });
      setAdminPin(pin.trim());
      setPin("");
    } catch (err) {
      setError(err.response?.status === 403 ? "Wrong PIN. Please try again." : "Unable to verify the PIN.");
    } finally { setBusy(false); }
  };

  if (unlocked) return <Outlet />;

  return (
    <div className="min-h-[70vh] flex items-center justify-center" data-testid="admin-gate">
      <form onSubmit={submit} className="w-full max-w-sm border border-border rounded-sm bg-card p-8 space-y-6 relative overflow-hidden">
        <ShieldCheck size={140} weight="fill" className="absolute -right-8 -bottom-8 text-primary/10" />
        <div className="relative">
          <div className="w-12 h-12 rounded-sm bg-primary/15 border border-primary/40 flex items-center justify-center text-primary mb-4"><LockKey size={26} weight="fill" /></div>
          <p className="uppercase text-xs tracking-[0.2em] font-bold text-primary">Restricted area</p>
          <h1 className="font-display font-black uppercase tracking-tighter text-3xl mt-1">Admin</h1>
          <p className="text-sm text-muted-foreground mt-2">Enter the PIN to access user management and access monitoring.</p>
        </div>
        <div className="space-y-2 relative">
          <Input data-testid="admin-pin-input" type="password" inputMode="numeric" autoFocus value={pin} onChange={(e) => setPin(e.target.value)} placeholder="PIN" className="text-center text-2xl tracking-[0.5em] font-display h-14" />
          {error && <p className="text-sm text-red-400" data-testid="admin-pin-error">{error}</p>}
        </div>
        <Button type="submit" disabled={busy} data-testid="admin-pin-submit" className="w-full font-display uppercase tracking-wide h-11">{busy ? "Checking…" : "Unlock"}</Button>
      </form>
    </div>
  );
}
